
import React, { useState } from 'react';
import { AssessmentResult } from '../types';
import { downloadAsPDF, downloadAsWord } from '../utils/exportUtils';

interface Props {
  result: AssessmentResult;
}

const ResultCard: React.FC<Props> = ({ result }) => {
  const [isExporting, setIsExporting] = useState(false);

  // Helper to safely ensure we have an array of strings
  const safeArray = (val: any) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim()) return [val];
    return [];
  };

  // Ultra-robust normalization to prevent runtime errors
  const criteriaResults = (result && Array.isArray(result.criteriaResults)) ? result.criteriaResults : [];
  const generalStrengths = safeArray(result?.generalStrengths);
  const areasForImprovement = safeArray(result?.areasForImprovement);

  const spread = (result.maxMark || result.overallMark || 0) - (result.minMark || result.overallMark || 0);
  const hasVisualCriteria = criteriaResults.some(c => c && c.isVisual);
  
  const getConfidenceLevel = () => {
    if (spread <= 3) return { label: 'High Confidence', color: 'text-uon-green', bg: 'bg-uon-green/10', border: 'border-uon-green' };
    if (spread <= 8) return { label: 'Moderate Confidence', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-400' };
    return { label: 'Low Confidence', color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-400' };
  };

  const confidence = getConfidenceLevel();

  const getMarkColorClass = (mark: number) => {
    if (mark >= 70) return 'text-uon-green bg-emerald-50 border-uon-green/30';
    if (mark >= 60) return 'text-uon-sky bg-blue-50 border-uon-sky/30';
    if (mark >= 50) return 'text-uon-blue bg-uon-light/30 border-uon-blue/30';
    if (mark >= 40) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-rose-700 bg-rose-50 border-rose-200';
  };

  const getProgressColorClass = (mark: number) => {
    if (mark >= 70) return 'bg-uon-green';
    if (mark >= 60) return 'bg-uon-sky';
    if (mark >= 50) return 'bg-uon-blue';
    if (mark >= 40) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  const handleWordExport = async () => {
    setIsExporting(true);
    await downloadAsWord(result);
    setIsExporting(false);
  };

  return (
    <div className="bg-white rounded-[3rem] shadow-2xl shadow-slate-200/50 border border-slate-200 overflow-hidden group transition-all duration-500 hover:shadow-uon-light/50 relative">
      {/* Draft Watermark */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 -rotate-12 opacity-[0.03] pointer-events-none select-none z-0">
        <span className="text-[12rem] font-black tracking-tighter">DRAFT</span>
      </div>

      {/* Header Section */}
      <div className="p-8 lg:p-12 border-b-2 border-slate-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-10 bg-slate-50/20 relative z-10">
        <div className="space-y-6 flex-1 min-w-0">
          <div className="space-y-2">
            <h3 className="text-4xl font-black text-slate-900 tracking-tight leading-tight truncate">
              {result.studentName || result.fileName}
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] bg-uon-gold text-uon-blue px-3 py-1 rounded shadow-sm">DRAFT ASSESSMENT</span>
              {result.studentID && (
                <span className="text-xs font-black uppercase tracking-widest bg-uon-light/50 text-uon-blue px-3 py-1 rounded-full border border-uon-light">
                  ID: {result.studentID}
                </span>
              )}
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${confidence.bg} ${confidence.border} ${confidence.color} text-[10px] font-black uppercase tracking-widest shadow-sm`}>
                <span className={`h-2 w-2 rounded-full ${confidence.label === 'High Confidence' ? 'bg-uon-green' : confidence.label === 'Moderate Confidence' ? 'bg-amber-500' : 'bg-rose-500'}`}></span>
                {confidence.label} ({result.minMark || 0}% – {result.maxMark || 0}%)
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button onClick={() => downloadAsPDF(result)} className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-700 text-sm font-black uppercase tracking-widest hover:border-uon-sky hover:text-uon-sky transition-all shadow-sm">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              PDF Report
            </button>
            <button onClick={handleWordExport} disabled={isExporting} className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-700 text-sm font-black uppercase tracking-widest hover:border-uon-sky hover:text-uon-sky transition-all shadow-sm disabled:opacity-50">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Word Document
            </button>
          </div>
        </div>
        
        <div className="flex gap-4">
          <div className={`shrink-0 min-w-[180px] p-6 rounded-[2rem] border-4 text-center shadow-xl transition-transform hover:scale-105 bg-white border-slate-100`}>
            <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 opacity-60">Draft Mark</div>
            <div className="text-5xl font-black leading-none mb-1 tracking-tighter text-slate-800">{result.overallMark || 0}%</div>
            <div className="text-xs font-black text-uon-sky mb-1">
              {result.totalEarned || 0} / {result.totalMax || 0} Raw
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest leading-none opacity-50">{result.classification || 'N/A'}</div>
          </div>

          {hasVisualCriteria && (
            <div className={`shrink-0 min-w-[180px] p-6 rounded-[2rem] border-4 text-center shadow-xl transition-transform hover:scale-105 ${getMarkColorClass(result.adjustedMark || 0)}`}>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 opacity-80">Excl. Visuals</div>
              <div className="text-5xl font-black leading-none mb-1 tracking-tighter">{result.adjustedMark || result.overallMark || 0}%</div>
              <div className="text-[8px] font-black uppercase tracking-widest leading-none opacity-60 flex items-center justify-center gap-1">
                <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                Pro-Rata
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x-2 divide-slate-100 relative z-10 bg-white/80 backdrop-blur-sm">
        <div className="p-8 lg:p-12 space-y-10">
          <div className="flex items-center justify-between gap-4">
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.25em] shrink-0">Criteria Mapping</h4>
            {hasVisualCriteria && (
              <span className="text-[9px] font-black uppercase bg-uon-sky/10 text-uon-sky px-2 py-0.5 rounded border border-uon-sky/20">
                Visual Items Flagged (N/A)
              </span>
            )}
            <div className="flex-1 h-px bg-slate-100"></div>
          </div>
          
          <div className="space-y-12">
            {criteriaResults.map((crit, idx) => (
              <div key={idx} className={`space-y-4 ${crit && crit.isVisual ? 'opacity-70 grayscale-[0.5]' : ''}`}>
                <div className="flex justify-between items-end gap-6">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-uon-sky uppercase tracking-widest">Criterion {idx + 1}</span>
                      {crit && crit.isVisual && (
                        <span className="text-[9px] font-black bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full uppercase tracking-widest">Visual Component</span>
                      )}
                    </div>
                    <h5 className="text-xl font-black text-slate-800 leading-tight">{crit ? crit.criterion : 'Criterion Error'}</h5>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`block text-3xl font-black leading-none ${crit && crit.isVisual ? 'text-slate-400 line-through decoration-2' : 'text-slate-900'}`}>{crit ? crit.mark : 0}</span>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">of {crit ? crit.maxMark : 0}</span>
                  </div>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden shadow-inner flex">
                  <div 
                    className={`h-full transition-all duration-1000 ${crit && crit.isVisual ? 'bg-slate-300' : getProgressColorClass(((crit ? crit.mark : 0)/(crit ? (crit.maxMark || 1) : 1))*100)}`} 
                    style={{ width: `${((crit ? crit.mark : 0) / (crit && crit.maxMark ? crit.maxMark : 100)) * 100}%` }}
                  ></div>
                </div>
                <div className={`p-6 rounded-2xl border transition-all shadow-inner ${crit && crit.isVisual ? 'bg-slate-50 border-slate-200' : 'bg-slate-50/50 border-slate-100 hover:bg-white'}`}>
                  <p className="text-base text-slate-700 leading-relaxed font-semibold italic">
                    {crit && crit.isVisual ? `[N/A Component] ${crit.feedback}` : (crit ? crit.feedback : 'No feedback available.')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-8 lg:p-12 space-y-12 bg-slate-50/30">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.25em] shrink-0">Academic Summary</h4>
              <div className="flex-1 h-px bg-slate-100"></div>
            </div>
            <div className="bg-white rounded-[2.5rem] p-10 border-2 border-uon-light shadow-sm relative overflow-hidden group/sum">
              <div className="absolute top-0 right-0 p-8 text-uon-light/30">
                <svg className="h-12 w-12" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017V14H17.017C15.3601 14 14.017 12.6569 14.017 11V8H20.017V14L18.017 14V16H20.017C21.1216 16 22.017 16.8954 22.017 18V21H14.017ZM2.01697 21L2.01697 18C2.01697 16.8954 2.9124 16 4.01697 16H7.01697V14H5.01697C3.36012 14 2.01697 12.6569 2.01697 11V8H8.01697V14L6.01697 14V16H8.01697C9.12154 16 10.017 16.8954 10.017 18V21H2.01697Z" /></svg>
              </div>
              <p className="text-xl text-slate-800 leading-relaxed font-black relative z-10 italic">
                "{result.summaryFeedback || 'Summary being processed...'}"
              </p>
            </div>
          </div>

          <div className="grid gap-10">
            <div className="space-y-6">
              <h5 className="text-xs font-black text-uon-green uppercase tracking-[0.25em] flex items-center gap-3">
                <span className="w-6 h-6 rounded-lg bg-uon-green flex items-center justify-center text-[10px] text-white">✓</span>
                Academic Strengths
              </h5>
              <div className="grid gap-4">
                {generalStrengths.map((str, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-2xl border border-emerald-50 shadow-sm flex items-start gap-4 transform transition-transform hover:scale-[1.01]">
                    <span className="text-uon-green font-black text-lg">0{idx + 1}</span>
                    <p className="text-base font-bold text-slate-700 leading-snug">{str}</p>
                  </div>
                ))}
                {generalStrengths.length === 0 && <p className="text-slate-400 text-xs italic">No specific strengths listed.</p>}
              </div>
            </div>

            <div className="space-y-6">
              <h5 className="text-xs font-black text-rose-600 uppercase tracking-[0.25em] flex items-center gap-3">
                <span className="w-6 h-6 rounded-lg bg-rose-500 flex items-center justify-center text-[10px] text-white">!</span>
                Areas for Development
              </h5>
              <div className="grid gap-4">
                {areasForImprovement.map((area, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-2xl border border-rose-50 shadow-sm flex items-start gap-4 transform transition-transform hover:scale-[1.01]">
                    <span className="text-rose-500 font-black text-lg">0{idx + 1}</span>
                    <p className="text-base font-bold text-slate-700 leading-snug">{area}</p>
                  </div>
                ))}
                {areasForImprovement.length === 0 && <p className="text-slate-400 text-xs italic">No specific development areas listed.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultCard;
